import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApp } from '@/context/AppContext';
import { TopBar } from '@/components/TopBar';
import { Page } from '@/components/Page';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from '@/components/ui/drawer';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import api, { errMsg } from '@/lib/api';
import { toast } from 'sonner';
import { money, money2, fmtDate, STATUS_STYLES, PAYMENT_METHODS } from '@/lib/helpers';
import { Loader2, Wallet, Pencil, RotateCcw, AlertTriangle, Boxes, Printer, ScanLine, Calculator } from 'lucide-react';
import PurchaseItemsEditor from '@/components/PurchaseItemsEditor';
import PurchaseScanMove from '@/components/PurchaseScanMove';

export default function PurchaseDetail() {
  const { pid } = useParams();
  const navigate = useNavigate();
  const { user, stores } = useApp();
  const isOwner = user?.role === 'OWNER' || !!user?.owner_access;
  const storeName = (id) => stores.find((s) => s.id === id)?.name || '—';
  const qc = useQueryClient();
  const [payOpen, setPayOpen] = useState(false);
  const [pay, setPay] = useState({ amount: '', method: 'CASH' });
  const [busy, setBusy] = useState(false);
  // Edit-details drawer
  const [editOpen, setEditOpen] = useState(false);
  const [edit, setEdit] = useState({ invoice: '', amount: '', status: 'UNPAID', paid: '', notes: '' });
  const [savingEdit, setSavingEdit] = useState(false);
  // Undo / void
  const [voidOpen, setVoidOpen] = useState(false);
  const [voidReason, setVoidReason] = useState('');
  const [voiding, setVoiding] = useState(false);
  // Bulk item editor
  const [itemsOpen, setItemsOpen] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);
  const [recalcOpen, setRecalcOpen] = useState(false);
  const [recalcPreview, setRecalcPreview] = useState(null);
  const [recalcing, setRecalcing] = useState(false);
  const openRecalc = async () => {
    setRecalcing(true);
    try {
      const r = await api.post(`/purchases/${pid}/recalculate`, { preview: true });
      setRecalcPreview(r.data.data);
      setRecalcOpen(true);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setRecalcing(false); }
  };
  const applyRecalc = async () => {
    if (recalcing) return;
    setRecalcing(true);
    try {
      const r = await api.post(`/purchases/${pid}/recalculate`, {});
      const d = r.data.data;
      if (d.skipped) { toast.info('No linked garments — value left unchanged and flagged.'); }
      else { toast.success(`Value set to ${money(d.value)} from ${d.garment_count} garment${d.garment_count === 1 ? '' : 's'}`); }
      qc.invalidateQueries({ queryKey: ['purchase', pid] });
      setRecalcOpen(false);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setRecalcing(false); }
  };
  const { data, isLoading } = useQuery({ queryKey: ['purchase', pid], queryFn: async () => (await api.get(`/purchases/${pid}`)).data.data });

  const p = data?.purchase;
  const garments = data?.garments || [];
  const badge = { PAID: 'bg-emerald-50 text-emerald-800', PARTIAL: 'bg-amber-50 text-amber-800', UNPAID: 'bg-rose-50 text-rose-700' };

  const recordPayment = async () => {
    if (!pay.amount) { toast.error('Enter amount.'); return; }
    setBusy(true);
    try {
      await api.post(`/purchases/${pid}/payment`, { amount: Number(pay.amount), method: pay.method });
      toast.success('Payment recorded');
      setPayOpen(false); setPay({ amount: '', method: 'CASH' });
      qc.invalidateQueries({ queryKey: ['purchase', pid] });
      qc.invalidateQueries({ queryKey: ['purchases'] });
      qc.invalidateQueries({ queryKey: ['suppliers'] });
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const openEdit = () => {
    setEdit({
      invoice: p.supplier_invoice || '',
      amount: String(p.amount ?? ''),
      status: p.payment_status || 'UNPAID',
      paid: String(p.paid ?? ''),
      notes: p.notes || '',
    });
    setEditOpen(true);
  };

  const saveEdit = async () => {
    const amountN = Number(edit.amount || 0);
    if (isNaN(amountN) || amountN < 0) { toast.error('Enter a valid purchase amount.'); return; }
    // Derive the paid figure from the chosen status so the balance stays consistent.
    let paidN = 0;
    if (edit.status === 'PAID') paidN = amountN;
    else if (edit.status === 'PARTIAL') paidN = Math.min(amountN, Math.max(0, Number(edit.paid || 0)));
    setSavingEdit(true);
    try {
      await api.put(`/purchases/${pid}`, {
        supplier_invoice: edit.invoice, amount: amountN, paid: paidN, notes: edit.notes,
      });
      toast.success('Purchase details updated');
      setEditOpen(false);
      qc.invalidateQueries({ queryKey: ['purchase', pid] });
      qc.invalidateQueries({ queryKey: ['purchases'] });
      qc.invalidateQueries({ queryKey: ['suppliers'] });
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSavingEdit(false); }
  };

  const doVoid = async () => {
    setVoiding(true);
    try {
      const r = await api.post(`/purchases/${pid}/void`, { reason: voidReason });
      toast.success(`Purchase ${r.data.data.voided} undone · ${r.data.data.removed_garments} item(s) removed from stock`);
      setVoidOpen(false);
      qc.invalidateQueries({ queryKey: ['purchases'] });
      qc.invalidateQueries({ queryKey: ['suppliers'] });
      qc.invalidateQueries({ queryKey: ['garments'] });
      navigate('/purchases');
    } catch (e) { toast.error(errMsg(e), { duration: 8000 }); }
    finally { setVoiding(false); }
  };

  const onItemsSaved = () => {
    qc.invalidateQueries({ queryKey: ['purchase', pid] });
    qc.invalidateQueries({ queryKey: ['purchases'] });
    qc.invalidateQueries({ queryKey: ['garments'] });
    qc.invalidateQueries({ queryKey: ['stock-summary'] });
  };

  const undoMove = async (moveId) => {
    try {
      const r = await api.post(`/purchases/${pid}/moves/${moveId}/undo`);
      const d = r.data.data;
      toast.success(`Reversed · ${d.restored} item(s) restored${d.skipped ? ` · ${d.skipped} skipped` : ''}`);
      onItemsSaved();
    } catch (e) { toast.error(errMsg(e), { duration: 7000 }); }
  };

  const redoMove = async (moveId) => {
    try {
      const r = await api.post(`/purchases/${pid}/moves/${moveId}/redo`);
      const d = r.data.data;
      toast.success(`Re-applied · ${d.applied} item(s)${d.skipped ? ` · ${d.skipped} skipped` : ''}`);
      onItemsSaved();
    } catch (e) { toast.error(errMsg(e), { duration: 7000 }); }
  };

  const printList = () => {
    const esc = (s) => String(s ?? '').replace(/[<>&]/g, (c) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;' }[c]));
    const rowsHtml = garments.map((g, i) => `<tr><td>${i + 1}</td><td>${esc(g.sr_no)}</td><td>${esc(g.barcode)}</td><td>${esc(g.product)}</td><td>${esc(g.category)}</td><td>${esc(g.store_name)}</td><td style="text-align:right">${esc(g.price)}</td></tr>`).join('');
    const w = window.open('', '_blank');
    if (!w) { toast.error('Allow pop-ups to print the list.'); return; }
    w.document.write(`<html><head><title>${esc(p.purchase_no)} items</title><style>body{font-family:Arial,Helvetica,sans-serif;padding:16px;color:#111}h2{margin:0 0 2px}.meta{color:#555;font-size:12px;margin-bottom:8px}table{width:100%;border-collapse:collapse;font-size:12px}th,td{border:1px solid #ccc;padding:6px;text-align:left}th{background:#f4f4f4}</style></head><body><h2>${esc(p.supplier_name)} — ${esc(p.purchase_no)}</h2><div class="meta">Invoice: ${esc(p.supplier_invoice) || '—'} · ${garments.length} item(s)</div><table><thead><tr><th>#</th><th>Sr No</th><th>Item Code</th><th>Name</th><th>Category</th><th>Store</th><th>Sale &#8377;</th></tr></thead><tbody>${rowsHtml}</tbody></table></body></html>`);
    w.document.close(); w.focus(); setTimeout(() => w.print(), 250);
  };

  if (isLoading || !p) return <Page noNav><TopBar title="Purchase" back showStore={false} /><div className="flex justify-center pt-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div></Page>;

  return (
    <Page noNav>
      <TopBar title={p.purchase_no} back showStore={false} />
      <div className="px-4 pt-4 space-y-4">
        <div className="rounded-2xl border border-border bg-card p-4">
          <div className="flex items-start justify-between gap-2">
            <div><h2 className="font-display text-lg font-semibold">{p.supplier_name}</h2><p className="text-xs text-muted-foreground">{p.supplier_invoice || '—'} · {fmtDate(p.created_at)}</p></div>
            <div className="flex shrink-0 flex-col items-end gap-1.5">
              <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${badge[p.payment_status]}`}>{p.payment_status}</span>
              {p.status !== 'VOIDED' && (
                <Button size="sm" variant="outline" className="h-7 rounded-lg px-2 text-[11px]" onClick={openRecalc} disabled={recalcing} data-testid="purchase-auto-calculate">
                  {recalcing && !recalcOpen ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <Calculator className="mr-1 h-3 w-3" />}Auto Calculate
                </Button>
              )}
            </div>
          </div>
          <div className="mt-3 grid grid-cols-3 gap-2 text-center text-sm">
            <div className="rounded-xl bg-secondary/50 p-2"><p className="text-xs text-muted-foreground">Value</p><p className="font-mono font-semibold">{money(p.amount)}</p></div>
            <div className="rounded-xl bg-secondary/50 p-2"><p className="text-xs text-muted-foreground">Paid</p><p className="font-mono font-semibold">{money(p.paid)}</p></div>
            <div className="rounded-xl bg-secondary/50 p-2"><p className="text-xs text-muted-foreground">Due</p><p className="font-mono font-semibold text-rose-700">{money(p.outstanding)}</p></div>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {p.outstanding > 0 && <Button className="h-11 flex-1 rounded-xl" onClick={() => setPayOpen(true)} data-testid="purchase-detail-record-payment"><Wallet className="mr-2 h-4 w-4" />Record Payment</Button>}
            <Button variant="secondary" className="h-11 flex-1 rounded-xl" onClick={openEdit} data-testid="purchase-detail-edit"><Pencil className="mr-2 h-4 w-4" />Edit Details</Button>
            <Button variant="secondary" className="h-11 flex-1 rounded-xl" onClick={() => setItemsOpen(true)} data-testid="purchase-detail-edit-items"><Boxes className="mr-2 h-4 w-4" />Edit Items</Button>
            <Button variant="secondary" className="h-11 flex-1 rounded-xl" onClick={() => setScanOpen(true)} data-testid="purchase-detail-scan-move"><ScanLine className="mr-2 h-4 w-4" />Scan &amp; Move</Button>
            {isOwner && (
              <Button variant="outline" className="h-11 flex-1 rounded-xl border-destructive/40 text-destructive hover:bg-destructive/10" onClick={() => { setVoidReason(''); setVoidOpen(true); }} data-testid="purchase-detail-void"><RotateCcw className="mr-2 h-4 w-4" />Undo / Void</Button>
            )}
          </div>
        </div>

        {(p.payments && p.payments.length > 0) && (
          <div className="rounded-2xl border border-border bg-card p-4">
            <p className="mb-2 text-sm font-semibold">Payment history</p>
            <div className="space-y-1.5">
              {p.payments.map((pay, i) => (
                <div key={i} className="flex justify-between text-sm" data-testid={`purchase-payment-${i}`}>
                  <span className="text-muted-foreground">{pay.method} · {fmtDate(pay.ts)}</span>
                  <span className="font-mono font-medium">{money2(pay.amount)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {p.moves?.length ? (
          <div data-testid="purchase-move-history">
            <p className="mb-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">Move history ({p.moves.length})</p>
            <div className="space-y-2">
              {[...p.moves].reverse().slice(0, 20).map((m) => (
                <div key={m.id} className={`rounded-xl border border-border bg-card p-3 text-sm ${m.undone ? 'opacity-60' : ''}`} data-testid={`purchase-move-${m.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium">{m.count} item(s) updated</span>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">{fmtDate(m.ts)}</span>
                      {m.undone ? (
                        <div className="flex items-center gap-1.5">
                          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-600" data-testid={`purchase-move-undone-${m.id}`}>Undone</span>
                          <Button variant="ghost" size="sm" className="h-7 rounded-lg px-2 text-xs text-primary hover:bg-primary/10" onClick={() => redoMove(m.id)} data-testid={`purchase-move-redo-${m.id}`}><RotateCcw className="mr-1 h-3 w-3 -scale-x-100" />Redo</Button>
                        </div>
                      ) : (
                        <Button variant="ghost" size="sm" className="h-7 rounded-lg px-2 text-xs text-primary hover:bg-primary/10" onClick={() => undoMove(m.id)} data-testid={`purchase-move-undo-${m.id}`}><RotateCcw className="mr-1 h-3 w-3" />Undo</Button>
                      )}
                    </div>
                  </div>
                  <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
                    {m.to_store ? <span>Store: {(m.from_stores || []).map(storeName).join(', ') || '—'} → <b className="text-foreground">{storeName(m.to_store)}</b></span> : null}
                    {m.price != null ? <span>Sale → <b className="text-foreground">{money(m.price)}</b></span> : null}
                    {m.cost != null ? <span>Purchase → <b className="text-foreground">{money(m.cost)}</b></span> : null}
                    <span>by {m.by}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        <div>
          <div className="mb-2 flex items-center justify-between">
            <p className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Garments ({garments.length})</p>            <Button variant="outline" size="sm" className="h-8 rounded-lg" onClick={printList} data-testid="purchase-print-list"><Printer className="mr-1.5 h-3.5 w-3.5" />Print list</Button>
          </div>
          <div className="space-y-2">
            {garments.map((g) => (
              <div key={g.id} className="flex items-center justify-between rounded-xl border border-border bg-card p-3" data-testid={`purchase-garment-${g.barcode}`}>
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium">{g.product}</p>
                  <p className="font-mono text-xs text-muted-foreground">
                    {g.sr_no ? <span className="mr-1 rounded bg-secondary px-1.5 py-0.5 text-[10px] font-semibold text-foreground" data-testid={`purchase-garment-sr-${g.barcode}`}>Sr {g.sr_no}</span> : null}
                    {g.barcode} · {g.store_name}
                  </p>
                </div>
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${STATUS_STYLES[g.status] || 'bg-slate-100'}`}>{g.status}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <PurchaseItemsEditor open={itemsOpen} onOpenChange={setItemsOpen} purchaseId={pid} garments={garments} onSaved={onItemsSaved} />
      <PurchaseScanMove open={scanOpen} onOpenChange={setScanOpen} purchaseId={pid} garments={garments} onSaved={onItemsSaved} />

      <Drawer open={payOpen} onOpenChange={setPayOpen}>
        <DrawerContent className="bg-card">
          <div className="mx-auto my-2 h-1 w-10 rounded-full bg-border" />
          <DrawerHeader><DrawerTitle>Record payment</DrawerTitle></DrawerHeader>
          <div className="space-y-3 px-4 pb-6">
            <p className="text-sm text-muted-foreground">Outstanding: <span className="font-mono font-semibold text-foreground">{money(p.outstanding)}</span></p>
            <div><Label>Amount</Label><Input inputMode="numeric" value={pay.amount} onChange={(e) => setPay({ ...pay, amount: e.target.value })} className="mt-1 h-11 rounded-xl" data-testid="purchase-pay-amount" /></div>
            <div><Label>Method</Label><Select value={pay.method} onValueChange={(v) => setPay({ ...pay, method: v })}><SelectTrigger className="mt-1 h-11 rounded-xl"><SelectValue /></SelectTrigger><SelectContent>{PAYMENT_METHODS.filter((m) => m !== 'CREDIT').map((m) => (<SelectItem key={m} value={m}>{m}</SelectItem>))}</SelectContent></Select></div>
            <Button className="h-12 w-full rounded-xl" onClick={recordPayment} disabled={busy} data-testid="purchase-pay-save">{busy ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Record Payment'}</Button>
          </div>
        </DrawerContent>
      </Drawer>

      {/* Edit details (header only — items are never touched) */}
      <Drawer open={editOpen} onOpenChange={setEditOpen}>
        <DrawerContent className="bg-card">
          <div className="mx-auto my-2 h-1 w-10 rounded-full bg-border" />
          <DrawerHeader><DrawerTitle>Edit purchase details</DrawerTitle></DrawerHeader>
          <div className="space-y-3 px-4 pb-6">
            <p className="text-xs text-muted-foreground">Changes here update the invoice, amount, payment status &amp; notes only. The {garments.length} item(s) already in stock are not affected.</p>
            <div><Label>Supplier invoice no.</Label><Input value={edit.invoice} onChange={(e) => setEdit({ ...edit, invoice: e.target.value })} className="mt-1 h-11 rounded-xl" placeholder="e.g. KT-4587" data-testid="purchase-edit-invoice" /></div>
            <div><Label>Purchase amount (₹)</Label><Input inputMode="decimal" value={edit.amount} onChange={(e) => setEdit({ ...edit, amount: e.target.value })} className="mt-1 h-11 rounded-xl" data-testid="purchase-edit-amount" /></div>
            <div>
              <Label>Payment status</Label>
              <div className="mt-1 grid grid-cols-3 gap-2">
                {['PAID', 'PARTIAL', 'UNPAID'].map((s) => (
                  <button key={s} type="button" onClick={() => setEdit({ ...edit, status: s })} data-testid={`purchase-edit-status-${s}`}
                    className={`h-10 rounded-xl text-sm font-medium transition-colors ${edit.status === s ? 'bg-primary text-primary-foreground' : 'bg-secondary hover:bg-secondary/70'}`}>{s}</button>
                ))}
              </div>
            </div>
            {edit.status === 'PARTIAL' && (
              <div><Label>Amount paid (₹)</Label><Input inputMode="decimal" value={edit.paid} onChange={(e) => setEdit({ ...edit, paid: e.target.value })} className="mt-1 h-11 rounded-xl" data-testid="purchase-edit-paid" /></div>
            )}
            <div><Label>Notes</Label><Textarea value={edit.notes} onChange={(e) => setEdit({ ...edit, notes: e.target.value })} className="mt-1 rounded-xl" rows={2} data-testid="purchase-edit-notes" /></div>
            <Button className="h-12 w-full rounded-xl" onClick={saveEdit} disabled={savingEdit} data-testid="purchase-edit-save">{savingEdit ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Save changes'}</Button>
          </div>
        </DrawerContent>
      </Drawer>

      {/* Undo / Void — owner only, reverses stock + supplier balance */}
      <AlertDialog open={voidOpen} onOpenChange={setVoidOpen}>
        <AlertDialogContent data-testid="purchase-void-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-destructive" /> Undo this purchase?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove all <b>{garments.length}</b> item(s) from stock and reverse <b>{p.supplier_name}</b>&rsquo;s balance by <b>{money(p.amount)}</b>. It only works if none of the items have been sold, transferred or returned. You can then recreate the purchase from the wizard.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="px-1">
            <Label className="text-xs">Reason (optional)</Label>
            <Input value={voidReason} onChange={(e) => setVoidReason(e.target.value)} className="mt-1 h-11 rounded-xl" placeholder="e.g. wrong invoice / duplicate entry" data-testid="purchase-void-reason" />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="purchase-void-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); doVoid(); }} disabled={voiding}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="purchase-void-confirm">
              {voiding ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Undo purchase'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={recalcOpen} onOpenChange={(o) => { if (!recalcing) setRecalcOpen(o); }}>
        <AlertDialogContent data-testid="purchase-recalc-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Auto-calculate purchase value</AlertDialogTitle>
            <AlertDialogDescription>
              {recalcPreview?.no_garments
                ? 'This purchase has no linked garments, so the value cannot be auto-calculated. It will be left unchanged and flagged.'
                : 'This sets the purchase Value to the sum of its garments\u2019 cost prices and recalculates Due. The Paid amount and all payment records stay exactly as they are.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {recalcPreview && (
            <div className="space-y-2 py-1 text-sm" data-testid="purchase-recalc-preview">
              <div className="flex justify-between"><span className="text-muted-foreground">Garments</span><span className="font-mono font-medium">{recalcPreview.garment_count}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Calculated value</span><span className="font-mono font-semibold" data-testid="recalc-value">{money(recalcPreview.value)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Paid (unchanged)</span><span className="font-mono font-medium">{money(recalcPreview.paid)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">New due</span><span className="font-mono font-semibold text-rose-700">{money(recalcPreview.outstanding)}</span></div>
              {recalcPreview.missing_cost_count > 0 && (
                <div className="flex items-start gap-2 rounded-lg bg-amber-500/10 px-2.5 py-2 text-xs text-amber-700 dark:text-amber-300" data-testid="recalc-missing-flag">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>{recalcPreview.missing_cost_count} garment(s) have no cost price and are excluded from the total. Add their cost to include them.</span>
                </div>
              )}
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={recalcing} data-testid="recalc-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={applyRecalc} disabled={recalcing} data-testid="recalc-apply">
              {recalcing ? 'Applying…' : 'Apply'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Page>
  );
}
